(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-bsh-product-catchpoints]';
  const GUIDE_CARD_SELECTOR = '[data-bsh-appliance-guide] .bsh-appliance-card';
  const CTA_CLASS = 'bsh-product-catchpoint-cta';
  const ORDER_TARGET_ID = 'bsh-product-order-target';

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function firstElement(selectors, scope = document) {
    for (const selector of selectors) {
      const element = scope.querySelector(selector);
      if (element) return element;
    }
    return null;
  }

  function resolveOrderTarget() {
    const orderButton = firstElement([
      'product-info form[action*="/cart/add"] button[name="add"]',
      'product-info form[action*="/cart/add"] button[type="submit"]',
      'product-form form[action*="/cart/add"] button[name="add"]',
      'product-form form[action*="/cart/add"] button[type="submit"]',
      'form[action*="/cart/add"] button[name="add"]',
      'form[action*="/cart/add"] button[type="submit"]'
    ]);

    if (!orderButton) return null;

    const target =
      orderButton.closest('.product__info-container') ||
      orderButton.closest('product-info') ||
      orderButton.closest('.product') ||
      orderButton.parentElement ||
      orderButton;

    if (!target.id) target.id = ORDER_TARGET_ID;
    target.classList.add('bsh-product-order-target');

    return { target, orderButton };
  }

  function createOrderCta(text, targetId) {
    const wrapper = document.createElement('div');
    wrapper.className = CTA_CLASS;

    const link = document.createElement('a');
    link.className = 'bsh-product-catchpoint-button bsh-product-catchpoint-button--primary';
    link.href = `#${targetId}`;
    link.textContent = text;
    link.setAttribute('data-bsh-order-jump', '');

    wrapper.appendChild(link);
    return wrapper;
  }

  function appendOrderCta(host, text, targetId) {
    if (!host || host.querySelector(`:scope > .${CTA_CLASS}`)) return;
    host.appendChild(createOrderCta(text, targetId));
  }

  function addGuideCtas(root, orderTarget) {
    const text = root.dataset.orderText || 'Chci tento produkt';
    document.querySelectorAll(GUIDE_CARD_SELECTOR).forEach((card) => {
      appendOrderCta(card, text, orderTarget.target.id);
    });
  }

  function addUsefulAccessoriesCta(root, orderTarget) {
    const usefulLoader = document.querySelector(
      '[data-bsh-compatible-products][data-mode="useful"]'
    );
    if (!usefulLoader) return;

    const host =
      usefulLoader.closest('.bsh-appliance-card') ||
      usefulLoader.closest('section') ||
      usefulLoader.parentElement;

    appendOrderCta(
      host,
      root.dataset.orderText || 'Chci tento produkt',
      orderTarget.target.id
    );
  }

  function bindOrderJumps(root, orderTarget) {
    if (root.dataset.orderJumpBound === 'true') return;
    root.dataset.orderJumpBound = 'true';

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-bsh-order-jump]');
      if (!link) return;

      event.preventDefault();
      orderTarget.target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      window.setTimeout(() => {
        try {
          orderTarget.orderButton.focus({ preventScroll: true });
        } catch (_error) {
          orderTarget.orderButton.focus();
        }
      }, 650);
    });
  }

  function parseCollectionCandidates(root) {
    const script = root.querySelector('[data-bsh-product-collections]');
    if (!script) return [];

    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[Boschino catchpoints] Neplatný seznam kolekcí.', error);
      return [];
    }
  }

  function collectionFromPath() {
    const match = window.location.pathname.match(/^(.*?\/collections\/[^/]+)\/products\/[^/]+/i);
    if (!match) return null;
    return { url: match[1], source: 'path' };
  }

  function normalizeCollectionUrl(url) {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname.replace(/\/$/, '');
    } catch (_error) {
      return String(url).replace(/\/$/, '');
    }
  }

  function isGenericCollection(candidate) {
    const handle = String(candidate?.handle || '').toLowerCase();
    return [
      'all',
      'frontpage',
      'home-page',
      'homepage',
      'nahradni-dily',
      'prislusenstvi',
      'spotrebni-material'
    ].includes(handle);
  }

  function uniqueCandidates(candidates) {
    const seen = new Set();
    return candidates.filter((candidate) => {
      const url = normalizeCollectionUrl(candidate?.url);
      if (!url || seen.has(url)) return false;
      seen.add(url);
      candidate.url = url;
      return true;
    });
  }

  function buildCollectionCandidates(root) {
    const candidates = [];

    const configuredUrl = normalizeCollectionUrl(root.dataset.configuredCollectionUrl);
    if (configuredUrl) {
      candidates.push({
        url: configuredUrl,
        handle: configuredUrl.split('/').filter(Boolean).pop() || '',
        source: 'configured'
      });
    }

    const pathCollection = collectionFromPath();
    if (pathCollection) candidates.push(pathCollection);

    const productCollections = parseCollectionCandidates(root)
      .filter((candidate) => candidate && candidate.url)
      .sort((a, b) => {
        const genericDifference = Number(isGenericCollection(a)) - Number(isGenericCollection(b));
        if (genericDifference !== 0) return genericDifference;
        return String(b.handle || '').length - String(a.handle || '').length;
      });

    candidates.push(...productCollections);
    return uniqueCandidates(candidates);
  }

  async function fetchCollectionProducts(collectionUrl) {
    const endpoint = `${collectionUrl}/products.json?limit=250`;
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status} pro ${endpoint}`);
    }

    const payload = await response.json();
    return Array.isArray(payload?.products) ? payload.products : [];
  }

  async function resolveNextProduct(root) {
    const nextLink = root.querySelector('[data-bsh-next-product]');
    if (!nextLink) return;

    const currentHandle = root.dataset.currentHandle || '';
    if (!currentHandle) return;

    const candidates = buildCollectionCandidates(root);

    for (const candidate of candidates) {
      try {
        const products = await fetchCollectionProducts(candidate.url);
        if (products.length < 2) continue;

        const currentIndex = products.findIndex(
          (product) => String(product.handle || '') === currentHandle
        );
        if (currentIndex < 0) continue;

        let nextIndex = (currentIndex + 1) % products.length;
        if (products[nextIndex]?.handle === currentHandle) continue;

        const nextProduct = products[nextIndex];
        if (!nextProduct?.handle) continue;

        nextLink.href = `${candidate.url}/products/${encodeURIComponent(nextProduct.handle)}`;
        nextLink.hidden = false;
        nextLink.dataset.collectionUrl = candidate.url;
        nextLink.dataset.nextHandle = nextProduct.handle;
        nextLink.setAttribute(
          'aria-label',
          `${root.dataset.nextText || 'Další produkt'}: ${nextProduct.title || ''}`.trim()
        );
        return;
      } catch (error) {
        console.warn('[Boschino catchpoints] Kolekci se nepodařilo načíst.', candidate.url, error);
      }
    }

    nextLink.hidden = true;
  }

  function bindBackToTop(root) {
    const button = root.querySelector('[data-bsh-back-to-top]');
    if (!button || button.dataset.bound === 'true') return;
    button.dataset.bound = 'true';

    button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  function init() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    const orderTarget = resolveOrderTarget();
    if (!orderTarget) {
      console.warn('[Boschino catchpoints] Tlačítko Objednat nebylo nalezeno.');
      return;
    }

    const enhance = () => {

      addGuideCtas(root, orderTarget);
      addUsefulAccessoriesCta(root, orderTarget);
    };

    enhance();
    bindOrderJumps(root, orderTarget);
    bindBackToTop(root);
    resolveNextProduct(root);

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        enhance();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  ready(init);
})();
